/**
 * @function ParticipantCardHeader
 * @description Component for displaying a participant's card header.
 * @returns {JSX.Element | null} - The ParticipantCardHeader component.
 */
import AIBadge from '../../assets/ai-badge.svg'
import BackIcon from '../../assets/back.svg'
import { formatOutput } from '../../utils/formatOutput'
import CallButton from '../CallButton'
import Tabs from '../elements/Tabs'
import ReviewButton from '../ReviewButton'

/**
 * @function formatID
 * @description Formats an ID with a prefix.
 * @param {string} prefix - The prefix to format.
 * @param {string} id - The ID to format.
 * @returns {JSX.Element} - The formatted ID.
 */
function formatID(prefix: string, id: string) {
    return formatOutput(prefix + '–' + id.split('-')[0])
}

/**
 * @function ParticipantCardHeader
 * @description Component for displaying a participant's card header.
 * @returns {JSX.Element | null} - The ParticipantCardHeader component.
 */
export function ParticipantCardHeader({ participant }: { participant: Novoic.API.Participant }) {
    const tab = location.pathname.split('/').pop()

    const study = participant.studies[0]
    const externalId = study?.external_id

    return (
        <div className="divide-y divide-gray-200">
            <div className="py-7">
                <div className="flex items-center h-full px-8">
                    <BackIcon
                        onClick={() => history.back()}
                        className="cursor-pointer mr-3 fill-gray-500 hover:fill-gray-900 transition-colors"
                    />
                    <span className="text-2xl font-semibold mr-3">
                        {participant.name || formatID('PID', externalId || participant.id)}
                    </span>
                    {participant.name && (
                        <span className="text-2xl font-normal text-gray-600 mr-3">
                            {formatID('PID', externalId || participant.id)}
                        </span>
                    )}
                    {participant.assignment?.status !== 'incomplete' &&
                        participant.assignment?.recommend && (
                            <p className="bg-indigo-50 rounded-[32px] py-2 pl-2 pr-4 flex items-center">
                                <AIBadge className="mr-2" />
                                <span className="text-xs text-indigo-800 font-medium">
                                    AI Recommended
                                </span>
                            </p>
                        )}
                </div>
            </div>

            <div className="grid grid-cols-2 divide-x divide-gray-200">
                <div className="px-8 py-6">
                    <div className="flex gap-x-2">
                        <CallButton
                            disabled={!participant.is_phone_verified}
                            participantId={participant.id}
                        />

                        <ReviewButton participantId={participant.id} />

                        <Tabs
                            className="ml-3"
                            tabs={[
                                {
                                    name: 'Profile',
                                    href: 'profile',
                                    current: tab === 'profile',
                                },
                                {
                                    name: 'Tests',
                                    href: 'tests',
                                    current: tab === 'tests',
                                },
                                {
                                    name: 'Events',
                                    href: 'events',
                                    current: tab === 'events',
                                },
                            ]}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}
